import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  NumberField,
  Select,
  Spinner,
  Text,
  useToast,
} from '@chatsdet/ui';
import type { OrgBillingMode, SetOrgBillingBody, SuperadminBillingView } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useSuperadminBilling } from '../../../hooks/useBillingData';
import type { OrganizationWithStats } from './useSuperadminData';

const MODE_ITEMS: Array<{ id: OrgBillingMode; label: string }> = [
  { id: 'contract', label: 'Contract (unlimited unless set)' },
  { id: 'self_serve', label: 'Self-serve (trial and plans)' },
];

const SECONDS_PER_HOUR = 3600;

const trialDateInputValue = (trialEndsAt: string | null | undefined) =>
  trialEndsAt ? trialEndsAt.slice(0, 10) : '';
const trialEndOfDayIso = (dateValue: string) =>
  dateValue ? new Date(`${dateValue}T23:59:59.000Z`).toISOString() : null;

type Draft = {
  mode: OrgBillingMode;
  monthStartDay: number | null;
  includedExecutions: number | null;
  includedAgentHours: number | null;
  trialDate: string;
};

function draftFromView(view: SuperadminBillingView): Draft {
  return {
    mode: view.billingMode,
    monthStartDay: view.contract?.contractMonthStartDay ?? 1,
    includedExecutions: view.contract?.includedExecutions ?? null,
    includedAgentHours:
      view.contract?.includedAgentSeconds == null
        ? null
        : view.contract.includedAgentSeconds / SECONDS_PER_HOUR,
    trialDate: trialDateInputValue(view.plan === 'trial' ? view.trialEndsAt : null),
  };
}

/** The `PUT /superadmin/billing/:orgId` body for a draft, or null while it is incomplete. */
function billingBodyFromDraft(draft: Draft): SetOrgBillingBody | null {
  if (draft.mode === 'self_serve') {
    return { billingMode: 'self_serve', trialEndsAt: trialEndOfDayIso(draft.trialDate) };
  }
  if (draft.monthStartDay === null) return null;
  return {
    billingMode: 'contract',
    contractMonthStartDay: draft.monthStartDay,
    includedExecutions: draft.includedExecutions === null ? null : Math.round(draft.includedExecutions),
    includedAgentSeconds:
      draft.includedAgentHours === null
        ? null
        : Math.round(draft.includedAgentHours * SECONDS_PER_HOUR),
  };
}

function useSetOrgBillingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orgId: string; body: SetOrgBillingBody }) => {
      const res = await fetch(`${API_URL}/superadmin/billing/${input.orgId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input.body),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update billing');
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['superadmin-billing', input.orgId] }).catch(() => {});
    },
  });
}

function BillingForm({
  view,
  draft,
  setDraft,
}: {
  view: SuperadminBillingView;
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
}) {
  const update = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <Flex direction="column" gap="md">
      <Text size="sm" className="text-muted-foreground">
        Now: {view.plan ?? 'contract'}, {view.access} access. Executions this period:{' '}
        {view.executions.used}. Agent hours: {view.agentHours.used}.
      </Text>
      <Select
        label="Billing mode"
        items={MODE_ITEMS}
        selectedKey={draft.mode}
        onSelectionChange={(key) => key && update({ mode: String(key) as OrgBillingMode })}
      />
      {draft.mode === 'contract' ? (
        <>
          <NumberField
            label="Month start day"
            description="Day of the month the billing period starts (1 to 28)."
            value={draft.monthStartDay}
            onChange={(monthStartDay) => update({ monthStartDay })}
            minValue={1}
            maxValue={28}
          />
          <NumberField
            label="Included executions"
            description="Per period. Leave empty for unlimited."
            value={draft.includedExecutions}
            onChange={(includedExecutions) => update({ includedExecutions })}
            minValue={0}
          />
          <NumberField
            label="Included agent hours"
            description="Per period. Leave empty for unlimited."
            value={draft.includedAgentHours}
            onChange={(includedAgentHours) => update({ includedAgentHours })}
            minValue={0}
            step={0.5}
          />
        </>
      ) : (
        <Flex direction="column" gap="sm">
          <Text className="text-sm text-muted-foreground">Trial end date</Text>
          <input
            data-control
            type="date"
            value={draft.trialDate}
            onChange={(e) => update({ trialDate: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm"
          />
          <Text size="sm" className="text-muted-foreground">
            Keeps an existing plan; otherwise starts a trial. Empty clears the trial end.
          </Text>
        </Flex>
      )}
    </Flex>
  );
}

function BillingEditor({
  org,
  view,
  onClose,
}: {
  org: OrganizationWithStats;
  view: SuperadminBillingView;
  onClose: () => void;
}) {
  const mutation = useSetOrgBillingMutation();
  const { toast } = useToast();
  const [draft, setDraft] = React.useState(() => draftFromView(view));
  const body = billingBodyFromDraft(draft);

  const save = () => {
    if (!body) return;
    mutation.mutate(
      { orgId: org.id, body },
      {
        onSuccess: () => {
          toast.success('Billing updated');
          onClose();
        },
      }
    );
  };

  return (
    <>
      <DialogContent>
        <BillingForm view={view} draft={draft} setDraft={setDraft} />
        {mutation.isError && (
          <Text size="sm" className="text-destructive">
            {mutation.error.message}
          </Text>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onPress={onClose} isDisabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="primary" onPress={save} isDisabled={!body || mutation.isPending}>
          Save
        </Button>
      </DialogFooter>
    </>
  );
}

export function OrgBillingDialog({
  org,
  onClose,
}: {
  org: OrganizationWithStats | null;
  onClose: () => void;
}) {
  const query = useSuperadminBilling(org?.id ?? null);

  return (
    <DialogTrigger isOpen={!!org} onOpenChange={(open) => !open && onClose()}>
      <span />
      <Dialog
        title={org ? `Billing for ${org.name}` : 'Billing'}
        description="Contract orgs are never blocked. Self-serve orgs follow their plan."
      >
        {org && query.data ? (
          // Seeded once per org: a background refetch must not reset edits.
          <BillingEditor key={org.id} org={org} view={query.data} onClose={onClose} />
        ) : (
          <DialogContent>
            {query.isError ? (
              <Text className="text-destructive">Failed to load billing.</Text>
            ) : (
              <Box className="flex justify-center py-6">
                <Spinner />
              </Box>
            )}
          </DialogContent>
        )}
      </Dialog>
    </DialogTrigger>
  );
}
